import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Toggle } from 'framework7-react';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';
import dayjs from 'dayjs';
import { groupBy } from 'lodash';
import pluralize from 'pluralize';

import API from '@api/API';

import {
  CurrentEntertainer,
  useCurrentEntertainerQuery,
  useFetchCurrentEntertainer,
} from '@queries/EntertainerQueries';
import { EventReview, FeedEvent, useEventQuery, useEventReviewsQuery } from '@queries/EventQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useAddEntertainerToEventMutation } from '@mutations/EntertainerMutations';
import { useUpdateEventMutation } from '@mutations/EventMutations';
import { useReprocessScheduleDateDetailsMutation } from '@mutations/UserScheduleSubmissionsMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';

import { EntertainerDashboardAction, useNavigate, useRouter } from '@router/index';

import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { uuid } from '@utilities/uuid';

import Button from '@components/buttons/Button';
import { Chip } from '@components/chips/Chip';
import { DynamicInputList } from '@components/form/CheckboxInputList';
import { DatePicker } from '@components/form/DatePicker';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { PopoverActionList } from '@components/layout/PopoverActionList/PopoverActionList';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { CreateEntertainerScheduleEventPopup } from '@screens/entertainer/components/CreateEntertainerScheduleEventPopup';

export interface EntertainerScheduleDateDetail {
  entertainer: { _id: string; title: string; contact: { instagram?: string } };
  date: string;
  dateDetails: {
    _id?: string;
    showName: string;
    venue: string;
    startTime?: string;
    img?: string;
    existingEventIds?: string[];
    isMultiShowMatch?: boolean;
    createdEventId?: string;
    flyerMediaId?: string;
    matchingParsedEvents?: {
      title?: string;
      startDate?: string;
      startTime?: string;
      venueName?: string;
      source?: string;
      externalId?: string;
      mediaId?: string;
      linkedEventId?: string;
    }[];
  };
}

const getLinkedEventIds = ({
  createdEventId,
  existingEventIds = [],
  isMultiShowMatch,
}: EntertainerScheduleDateDetail['dateDetails']): string[] => {
  if (createdEventId) {
    return [createdEventId];
  }

  if (existingEventIds.length === 1 || (isMultiShowMatch && existingEventIds.length)) {
    return existingEventIds;
  }

  return [];
};

export interface ScheduledSocialPost {
  key: string;
  title: string;
  primaryImageSlideUrl: string;
  events: FeedEvent[];
  entertainerScheduleDateDetails: EntertainerScheduleDateDetail[];
  recommendations?: string[];
  hiddenEventIds?: string[];
  seriesId?: string;
}

interface InstagramPostParams {
  caption: string;
  slides: { imgUrl: string; taggedUsers: string[] }[];
  collaborators?: string[];
}

const useScheduledSocialPostByCityId = (
  cityId: string,
  params?: { dateFilter?: string; referenceDate?: string; recommendations?: string[] },
) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['scheduled-social-post', cityId, params],
    queryFn: () => {
      const { dateFilter } = params || {};

      const queryParams = new URLSearchParams();

      if (dateFilter) {
        queryParams.append('dateFilter', dateFilter);
      }

      if (params?.referenceDate) {
        queryParams.append('referenceDate', params.referenceDate);
      }

      if (params?.recommendations) {
        params.recommendations.forEach((rec) => queryParams.append('recommendations', rec));
      }

      return API.get<{ scheduledSocialPost: ScheduledSocialPost; postParams: InstagramPostParams }>(
        `/v1/cities/${cityId}/event-social-post?${queryParams.toString()}`,
      );
    },
    enabled: !!cityId,
  });

  return {
    scheduledSocialPost: data?.scheduledSocialPost,
    postParams: data?.postParams,
    isScheduledSocialPostReady: isFetched,
    refetchScheduledSocialPost: refetch,
  };
};

const useUpdateAutomatedInstagramPostMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: async ({
      automatedInstagramPostKey,
      meta,
    }: {
      automatedInstagramPostKey: string;
      meta: Record<string, any>;
    }) => {
      return API.post(`/v1/automated-instagram-posts`, { automatedInstagramPostKey, meta });
    },
  });

  return {
    isUpdatingAutomatedInstagramPost: isPending,
    updateAutomatedInstagramPostAsync: mutateAsync,
  };
};

const useMarkRecurringEventAsEndedMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: async ({ eventId }: { eventId: string }) => {
      return API.post(`/v1/events/${eventId}/mark-recurring-as-ended`, {});
    },
  });

  return {
    isMarkingRecurringEventAsEnded: isPending,
    markRecurringEventAsEndedAsync: mutateAsync,
  };
};

export const ScheduledSocialPostTemplate: FC<{
  cardTitle: string;
  imageUrl: string;
  eventTitle: string;
  eventDetails: string;
  button?: {
    text: string;
    action: () => void;
  };
}> = ({ cardTitle, imageUrl, eventTitle, eventDetails, button }) => {
  return (
    <div className="p-2" style={{ borderRadius: 8, backgroundColor: 'rgba(255,255,255,.2)' }}>
      <div className="mb-2">{cardTitle}</div>
      <img
        src={imageUrl}
        alt={eventTitle}
        style={{ width: 'auto', height: 240, borderRadius: 4 }}
      />
      <div>
        <div style={{ marginBottom: -4 }}>
          <strong>{eventTitle}</strong>
        </div>
        {eventDetails && (
          <div>
            <span style={{ fontSize: '.7rem' }}>{eventDetails}</span>
          </div>
        )}
        {button && (
          <div className="pt-1">
            <Button
              variant="smooth"
              color="neutral"
              size="sm"
              onClick={button.action}
              style={{ borderRadius: 4 }}
            >
              {button.text}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const DeprecatedEventsHorizontalScrollItem: FC<{
  scheduledSocialPost: ScheduledSocialPost;
  handleAddFlyer: () => void;
  handleJoinCast: (
    eventId: string,
    cb: (createdEntertainer: Pick<CurrentEntertainer, '_id' | 'title'>) => void,
  ) => void;
  castOverride?: {
    action: (eventId: string) => void;
    text: string;
  };
}> = ({ scheduledSocialPost, handleAddFlyer, handleJoinCast, castOverride }) => {
  return (
    <div className="d-flex gap-1 pe-4">
      <ScheduledSocialPostTemplate
        cardTitle="Slide 1"
        imageUrl={scheduledSocialPost.primaryImageSlideUrl}
        eventTitle={scheduledSocialPost.title}
        eventDetails={'--'}
        button={{
          text: 'Add Flyer',
          action: handleAddFlyer,
        }}
      />
      {scheduledSocialPost?.events?.map((event, i) => (
        <ScheduledSocialPostTemplate
          key={`event-${event._id}`}
          cardTitle={`Slide ${i + 2}`}
          imageUrl={event.img ?? ''}
          eventTitle={event.title}
          eventDetails={event.timeDetails}
          button={
            castOverride
              ? { text: castOverride.text, action: () => castOverride.action(event._id) }
              : {
                  text: "I'm in the cast",
                  action: () => handleJoinCast(event._id, () => {}),
                }
          }
        />
      ))}
      <div
        className="p-2"
        style={{ borderRadius: 8, width: 260, backgroundColor: 'rgba(255,255,255,.2)' }}
      >
        <div className="mb-2">Add Flyer</div>
        <div
          className="d-flex align-items-center justify-content-center text-center"
          style={{
            borderRadius: 4,
            backgroundColor: 'rgba(0,0,0,.3)',
            height: 240,
          }}
          onClick={handleAddFlyer}
        >
          <div>
            <div className="fs-1">
              <FontAwesomeIcon name="image" />
            </div>
            <div>Upload flyer</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SlidePreviewsHorizontalScrollItem: FC<{
  postParams: InstagramPostParams;
  handleAddFlyer: () => void;
  handleFeaturedCopyPopup: (eventId: string, slideImageUrl: string) => void;
  recommendations: string[];
}> = ({ postParams, handleAddFlyer, handleFeaturedCopyPopup, recommendations }) => {
  const getSlideKey = (slide: InstagramPostParams['slides'][number]) => {
    const eventId = recommendations.find((eventId) => slide.imgUrl.includes(`events/${eventId}`));
    return eventId ? `recommended-event-slide-${eventId}` : slide.imgUrl;
  };

  const getSlideButtonProps = (slide: InstagramPostParams['slides'][number]) => {
    const eventId = recommendations.find((eventId) => slide.imgUrl.includes(`events/${eventId}`));

    return eventId
      ? {
          text: 'Edit Featured Copy',
          action: () => handleFeaturedCopyPopup(eventId, slide.imgUrl),
        }
      : {
          text: 'Add Flyer',
          action: handleAddFlyer,
        };
  };

  return (
    <div className="d-flex gap-1 pe-4">
      {postParams.slides.map((slide, i) => (
        <ScheduledSocialPostTemplate
          key={getSlideKey(slide)}
          cardTitle={`Slide ${i + 1}`}
          imageUrl={slide.imgUrl}
          eventTitle={'--'}
          eventDetails={'--'}
          button={getSlideButtonProps(slide)}
        />
      ))}
    </div>
  );
};

const useGenerateEventFeatureCopyMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: async ({ eventId }: { eventId: string }) => {
      return API.post<{ primaryComment: string; secondaryComments: string[] }, {}>(
        `/v1/events/${eventId}/feature-copy`,
        {},
      );
    },
  });

  return {
    isGeneratingEventFeatureCopy: isPending,
    generateEventFeatureCopyAsync: mutateAsync,
  };
};

const ReviewCard: FC<{
  review: EventReview;
  checked: (item: { key: string; text: string }) => boolean;
  onCheckChange: (key: string) => void;
  defaultComments?: { key: string; text: string }[];
  onChangeComments: (updatedComments: { key: string; text: string }[]) => void;
}> = ({ review, defaultComments = [], checked, onCheckChange, onChangeComments }) => {
  return (
    <div>
      <Card key={review._id}>
        <div className="p-3">
          {mapStringToParagraphs(review.comment, {
            collapseTopMargin: true,
            sharedClassName: 'my-0',
          })}
        </div>
      </Card>
      <div className={defaultComments.length ? 'mt-2' : ''}>
        <DynamicInputList
          addButton={{ text: 'Add Quote', defaultValue: review.comment }}
          defaultItems={defaultComments}
          checked={checked}
          onCheckChange={onCheckChange}
          onChange={onChangeComments}
        />
      </div>
    </div>
  );
};

const SlideFeaturedCopyPopupContent: FC<{
  eventId: string;
  slideImageUrl: string;
}> = ({ eventId, slideImageUrl: originalSlideImageUrl }) => {
  console.log({ eventId });
  const [primaryCommentKey, setPrimaryCommentKey] = useState<string>();
  const [customComments, setCustomComments] = useState<{ key: string; text: string }[]>([]);
  const { refetchEvent } = useEventQuery(eventId);
  const { isGeneratingEventFeatureCopy, generateEventFeatureCopyAsync } =
    useGenerateEventFeatureCopyMutation();
  const { isSubmitting, updateEventAsync } = useUpdateEventMutation({ eventId });
  const [slideImageUrl, setSlideImageUrl] = useState<string>(originalSlideImageUrl);
  const [uniqueKey, setUniqueKey] = useState<string>(uuid());
  const [commentsByReviewId, setCommentsByReviewId] =
    useState<Record<string, { key: string; text: string }[]>>();

  const { eventReviews } = useEventReviewsQuery(eventId, '');

  useEffect(() => {
    setSlideImageUrl(originalSlideImageUrl);
    (async () => {
      const { data } = await refetchEvent();

      if (data?.event?.featuredSlideQuotes) {
        const featuredSlideQuotes = [
          data?.event?.featuredSlideQuotes?.primaryComment,
          ...(data?.event?.featuredSlideQuotes?.secondaryComments || []),
        ].map((comment) => ({ key: uuid(), text: comment.text, reviewId: comment.reviewId }));
        const quotesWithReviews = featuredSlideQuotes.filter((quote) => quote.reviewId);
        const quotesWithoutReviews = featuredSlideQuotes.filter((quote) => !quote.reviewId);

        setCommentsByReviewId(groupBy(quotesWithReviews, 'reviewId'));
        setCustomComments(quotesWithoutReviews);
        setPrimaryCommentKey(featuredSlideQuotes?.[0]?.key);
      } else {
        const defaultComments = [
          data?.event?.featuredSlideCopy?.primaryComment,
          ...(data?.event?.featuredSlideCopy?.secondaryComments || []),
        ]
          .filter(Boolean)
          .map((text) => ({ key: uuid(), text }));

        setCustomComments(defaultComments);
        setPrimaryCommentKey(defaultComments[0]?.key);
      }

      setUniqueKey(uuid());
    })();
  }, []);

  const handleGenerateMoreCopy = async () => {
    if (!eventId) return;

    const { primaryComment, secondaryComments } = await generateEventFeatureCopyAsync({ eventId });
    const newComments = [primaryComment, ...(secondaryComments || [])]
      .filter(Boolean)
      .map((text) => ({ key: uuid(), text }));
    setCustomComments((prevComments) => [...prevComments, ...newComments]);
  };

  const handleSave = async () => {
    if (!eventId) return;

    const featuredSlideQuotes = [
      ...Object.entries(commentsByReviewId || {}).flatMap(([reviewId, comments]) =>
        comments.map((comment) => ({
          ...comment,
          reviewId,
        })),
      ),
      ...customComments,
    ];

    await updateEventAsync({
      featuredSlideQuotes: {
        primaryComment: featuredSlideQuotes.find((c) => c.key === primaryCommentKey) ?? {
          text: '',
        },
        secondaryComments: featuredSlideQuotes.filter((c) => c.key !== primaryCommentKey),
      },
    });

    setSlideImageUrl(`${slideImageUrl}&forceRegenerate=true&ui=${Date.now()}`);
  };

  const reviews = useMemo(() => {
    if (eventReviews?.length) {
      // mutate reverse
      return [...eventReviews].reverse();
    }

    return [];
  }, [eventReviews]);

  return (
    <Popup.Content key={uniqueKey}>
      <img key={slideImageUrl} src={slideImageUrl} className="w-100" />

      {Boolean(reviews.length) && (
        <div className="d-grid gap-2">
          {reviews.map((review) => (
            <ReviewCard
              key={`${review._id}-${uniqueKey}`}
              review={review}
              checked={(item) => primaryCommentKey === item.key}
              onCheckChange={(key) => setPrimaryCommentKey(key)}
              defaultComments={commentsByReviewId?.[review._id]}
              onChangeComments={(updatedComments) =>
                setCommentsByReviewId((prev) => ({ ...prev, [review._id]: updatedComments }))
              }
            />
          ))}
        </div>
      )}

      <div className="mt-4">
        <DynamicInputList
          key={`${eventId}-${slideImageUrl}`}
          defaultItems={customComments}
          checked={(item) => primaryCommentKey === item.key}
          onCheckChange={(key) => setPrimaryCommentKey(key)}
          onChange={(updatedItems) => setCustomComments(updatedItems)}
        />
      </div>
      <Button
        className="mt-2"
        roundness="rounded"
        color="neutral"
        variant="smooth"
        disabled={isGeneratingEventFeatureCopy}
        onClick={handleGenerateMoreCopy}
      >
        Generate More Copy
      </Button>
      <Button
        className="mt-2"
        roundness="rounded"
        disabled={isSubmitting || !primaryCommentKey}
        onClick={handleSave}
      >
        Save
      </Button>
    </Popup.Content>
  );
};

const SlideFeaturedCopyPopup: FC<{
  eventId: string;
  slideImageUrl: string;
  onClose: () => void;
}> = ({ eventId, slideImageUrl: originalSlideImageUrl, onClose }) => {
  const isVisible = !!originalSlideImageUrl && !!eventId;

  return (
    <Popup isVisible={isVisible} onClose={onClose} height="full">
      {isVisible && (
        <SlideFeaturedCopyPopupContent eventId={eventId} slideImageUrl={originalSlideImageUrl} />
      )}
    </Popup>
  );
};

const EntertainerScheduleDateDetailRow: FC<{
  entertainerScheduleDateDetail: ScheduledSocialPost['entertainerScheduleDateDetails'][number];
  onCreateEvent: (
    entertainerScheduleDateDetail: ScheduledSocialPost['entertainerScheduleDateDetails'][number],
  ) => void;
}> = ({ entertainerScheduleDateDetail, onCreateEvent }) => {
  const { navigate } = useRouter();

  return (
    <div>
      <div className="d-flex gap-1 align-items-center justify-content-between">
        <div className="d-grid">
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
            {[
              dayjs(entertainerScheduleDateDetail.date).format('ddd, MMM Do'),
              entertainerScheduleDateDetail.dateDetails.venue,
            ]
              .filter(Boolean)
              .join(' @ ')}
          </div>
          <div className="d-flex gap-2 align-items-center">
            <div>
              <strong>
                {entertainerScheduleDateDetail.dateDetails.showName ?? 'No show name'}
              </strong>
            </div>
            <div>{false && <Badge color="red">{1}</Badge>}</div>
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
            {entertainerScheduleDateDetail.entertainer.title ?? 'Missing entertainer name ⚠️'}
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <div>
            <PopoverActionList
              buttons={[
                {
                  text: 'View Instagram',
                  onClick: () => {
                    navigate(
                      `https://www.instagram.com/${entertainerScheduleDateDetail.entertainer.contact.instagram}`,
                    );
                  },
                },
                {
                  text: 'Create Event',
                  onClick: () => {
                    onCreateEvent(entertainerScheduleDateDetail);
                  },
                },
                // {
                //   text: 'Mark recurring as ended',
                //   onClick: handleMarkRecurringAsEnded,
                // },
                // {
                //   text: 'Hide event',
                //   onClick: handleHideEvent,
                // },
              ]}
            />
          </div>
        </div>
      </div>
      <div className="d-flex gap-1 align-items-center">
        {!!entertainerScheduleDateDetail.dateDetails.existingEventIds?.length && (
          <Chip
            size="sm"
            label={`${entertainerScheduleDateDetail.dateDetails.existingEventIds.length} existing`}
          />
        )}
        {!!entertainerScheduleDateDetail.dateDetails.matchingParsedEvents?.length && (
          <Chip
            size="sm"
            label={`${entertainerScheduleDateDetail.dateDetails.matchingParsedEvents.length} parsed`}
          />
        )}
        {!!entertainerScheduleDateDetail.dateDetails?.img && (
          <Chip size="sm" label="Flyer Uploaded" />
        )}
      </div>
    </div>
  );
};

const EventRow: FC<{
  defaultChecked: boolean;
  event: FeedEvent & { entertainerReviewsCount?: number };
  onToggleRecommendation: (eventId: string, isChecked: boolean) => void;
  onHideEvent: (eventId: string) => void;
  isScheduleDateDetailsMatch?: boolean;
}> = ({
  event,
  defaultChecked,
  onToggleRecommendation,
  onHideEvent,
  isScheduleDateDetailsMatch,
}) => {
  const { navigate } = useRouter();
  const { markRecurringEventAsEndedAsync } = useMarkRecurringEventAsEndedMutation();
  const [didRemove, setDidRemove] = useState(false);

  const handleToggleRecommendation = (isChecked: boolean) => {
    onToggleRecommendation(event._id, isChecked);
  };

  const { openConfirmDialog } = useDialog();
  const handleMarkRecurringAsEnded = () => {
    openConfirmDialog({
      title: 'Recurring Schedule Ended?',
      text: 'Are you sure you want to do this?',
      onConfirm: () => {
        setDidRemove(true);
        markRecurringEventAsEndedAsync({ eventId: event._id });
      },
    });
  };

  const handleHideEvent = () => {
    openConfirmDialog({
      title: 'Hide Event?',
      text: 'Are you sure you want to do this?',
      onConfirm: () => {
        setDidRemove(true);
        onHideEvent(event._id);
      },
    });
  };

  if (didRemove) {
    return <></>;
  }

  return (
    <div>
      <div className="d-flex gap-1 align-items-center justify-content-between">
        <div className="d-flex gap-2 align-items-center">
          <div>
            <strong>
              {event.title}
              {isScheduleDateDetailsMatch ? ' ⭐️' : ''}
            </strong>
          </div>
          <div>
            {!!event.entertainerReviewsCount && (
              <Badge color="red">{event.entertainerReviewsCount}</Badge>
            )}
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Toggle defaultChecked={defaultChecked} onToggleChange={handleToggleRecommendation} />
          <div>
            <PopoverActionList
              buttons={[
                {
                  text: 'View event',
                  onClick: () => {
                    navigate(`/events/${event._id}`);
                  },
                },
                {
                  text: 'Mark recurring as ended',
                  onClick: handleMarkRecurringAsEnded,
                },
                {
                  text: 'Hide event',
                  onClick: handleHideEvent,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export enum DateFilter {
  UpcomingWeek = 'upcoming-week',
  UpcomingWeekend = 'upcoming-weekend',
  UpcomingToday = 'upcoming-today',
  AllUpcoming = 'upcoming',
}

const DATE_FILTER_LABELS: Record<DateFilter, string> = {
  [DateFilter.UpcomingWeek]: 'Upcoming Week',
  [DateFilter.UpcomingWeekend]: 'Upcoming Weekend',
  [DateFilter.UpcomingToday]: 'Today',
  [DateFilter.AllUpcoming]: 'All Upcoming',
};

const DATE_FILTER_OPTIONS = Object.entries(DATE_FILTER_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export const ScheduledSocialPostSection: FC<{
  recommendationModeEnabled?: boolean;
  hideIndividualFeatureTitle?: boolean;
  cityId: string;
  description?: string;
  castOverride?: {
    action: (eventId: string) => void;
    text: string;
  };
  dateFilter?: DateFilter;
}> = ({
  hideIndividualFeatureTitle,
  cityId,
  description,
  castOverride,
  recommendationModeEnabled: initialRecommendationModeEnabled = false,
  dateFilter: initialDateFilter = 'upcoming-week',
}) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const [referenceDate, setReferenceDate] = useState<string>();
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [recommendationModeEnabled, setRecommendationModeEnabled] = useState(
    initialRecommendationModeEnabled,
  );
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const {
    scheduledSocialPost: currentScheduledSocialPost,
    postParams: currentPostParams,
    refetchScheduledSocialPost,
  } = useScheduledSocialPostByCityId(cityId, {
    dateFilter,
    referenceDate,
    recommendations: recommendationModeEnabled ? recommendations : undefined,
  });
  const { openGatedAddShowPopup } = useEntertainerEngagementContext();
  const { city } = useCityById(cityId);
  const { cities } = useCitiesQuery();
  const { openConfirmDialog } = useDialog();
  const { createAuthGateHandler } = useAuthGate();
  const { entertainer } = useCurrentEntertainerQuery();
  const fetchCurrentEntertainer = useFetchCurrentEntertainer();
  const { addEntertainerToEventAsync } = useAddEntertainerToEventMutation();
  const { isReprocessingScheduleDateDetails, reprocessScheduleDateDetailsAsync } =
    useReprocessScheduleDateDetailsMutation();
  const { isUpdatingAutomatedInstagramPost, updateAutomatedInstagramPostAsync } =
    useUpdateAutomatedInstagramPostMutation();
  const { currentUser } = useCurrentUser();
  const didInit = useRef(false);
  const [previousScheduledSocialPost, setPreviousScheduledSocialPost] =
    useState<ScheduledSocialPost>();
  const [previousPostParams, setPreviousPostParams] = useState<InstagramPostParams>();
  const [featuredCopy, setFeaturedCopy] = useState({
    eventId: '',
    slideImageUrl: '',
  });
  const [createEntertainerScheduleEventDateDetail, setCreateEntertainerScheduleEventDateDetail] =
    useState<EntertainerScheduleDateDetail>();

  const { scheduledSocialPost, postParams } = useMemo(
    () => ({
      scheduledSocialPost: currentScheduledSocialPost ?? previousScheduledSocialPost,
      postParams: currentPostParams ?? previousPostParams,
    }),
    [
      currentScheduledSocialPost,
      previousScheduledSocialPost,
      currentPostParams,
      previousPostParams,
    ],
  );

  useEffect(() => {
    if (currentScheduledSocialPost && !didInit.current) {
      didInit.current = true;
      setRecommendationModeEnabled(
        !!currentScheduledSocialPost?.recommendations?.length || initialRecommendationModeEnabled,
      );
      setRecommendations(currentScheduledSocialPost?.recommendations || []);
    }

    if (currentScheduledSocialPost) {
      setPreviousScheduledSocialPost(currentScheduledSocialPost);
    }
    if (currentPostParams) {
      setPreviousPostParams(currentPostParams);
    }
  }, [currentScheduledSocialPost, currentPostParams]);

  const entertainerScheduleDateDetails = useMemo(() => {
    return scheduledSocialPost?.entertainerScheduleDateDetails.filter(
      (date) => !getLinkedEventIds(date.dateDetails).length,
    );
  }, [scheduledSocialPost]);

  const scheduleDateDetailsLinkedEventIds = useMemo(
    () =>
      new Set(
        scheduledSocialPost?.entertainerScheduleDateDetails.flatMap((date) =>
          getLinkedEventIds(date.dateDetails),
        ) ?? [],
      ),
    [scheduledSocialPost],
  );

  if (!currentScheduledSocialPost && !didInit.current) {
    return <></>;
  }

  const slideCount = postParams?.slides?.length ?? 0;

  const handleAddFlyer = () => {
    track('Add IG Post Flyer');
    openGatedAddShowPopup({
      cityId,
      isEntertainerOptional: true,
      callback: refetchScheduledSocialPost,
    });
  };

  const handleJoinCast = (
    eventId: string,
    cb: (createdEntertainer: Pick<CurrentEntertainer, '_id' | 'title'>) => void,
  ) => {
    track('Join Cast', { eventId });
    openConfirmDialog({
      title: 'Join Cast?',
      text: "You're about to add yourself to the cast list for this show. This won't book you or change the show's lineup—it just helps keep the info accurate. Continue?",
      onConfirm: () => {
        createAuthGateHandler(
          async () => {
            let addedEntertainer = entertainer;

            if (!addedEntertainer?._id) {
              const { entertainer: refetchedEntertainer } = await fetchCurrentEntertainer();
              addedEntertainer = refetchedEntertainer;
            }

            await addEntertainerToEventAsync({ entertainerId: addedEntertainer?._id, eventId });
            await cb(addedEntertainer);
          },
          {
            entertainerCallback: () => {},
            cityId,
          },
        );
      },
      buttonOk: 'Join',
    });
  };

  const DEFAULT_DESCRIPTION = `We're spotlighting ${city?.name} shows that feature drag kings! Add your event to get reshared on Instagram.`;

  const handleUpdateRecommendation = async (eventId: string, isChecked: boolean) => {
    const updatedRecommendations = isChecked
      ? [...recommendations, eventId]
      : recommendations.filter((id) => id !== eventId);
    setRecommendations(updatedRecommendations);

    if (scheduledSocialPost) {
      await updateAutomatedInstagramPostAsync({
        automatedInstagramPostKey: scheduledSocialPost.key,
        meta: { recommendations: updatedRecommendations },
      });
    }

    refetchScheduledSocialPost();
  };

  const handleHideEvent = async (eventId: string) => {
    const updatedHiddenEventIds = [...(scheduledSocialPost?.hiddenEventIds || []), eventId];

    if (scheduledSocialPost) {
      await updateAutomatedInstagramPostAsync({
        automatedInstagramPostKey: scheduledSocialPost.key,
        meta: { hiddenEventIds: updatedHiddenEventIds },
      });
    }

    refetchScheduledSocialPost();
  };

  return (
    <>
      <TitleToolbar
        text={
          <span>
            <FontAwesomeIcon name="instagram" faStyle="fab" />
            <span className="ps-1">{`Upcoming ${city?.name} Post`}</span>
          </span>
        }
        className="mb-1"
        size="md"
        action={<span>{`${slideCount} ${pluralize('slide', slideCount)}`}</span>}
      />

      <p className="mt-0">{description ?? DEFAULT_DESCRIPTION}</p>

      {currentUser?.isSuperAdmin && (
        <div className="d-grid gap-1 mb-2">
          <div className="d-flex gap-1">
            <Select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              options={DATE_FILTER_OPTIONS}
            />
            <DatePicker
              value={referenceDate}
              onChange={(updatedValue) => {
                setReferenceDate(dayjs(updatedValue).format('YYYY-MM-DD'));
              }}
            />
          </div>
          <div className="d-flex gap-1 align-items-center justify-content-between">
            <div>
              <strong>Recommendations</strong>
            </div>
            <div>
              <Toggle
                checked={recommendationModeEnabled}
                onToggleChange={(updatedValue) => {
                  requestAnimationFrame(() => {
                    setRecommendationModeEnabled(updatedValue);
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {scheduledSocialPost && (
        <HorizontalScroll
          items={[
            postParams ? (
              <SlidePreviewsHorizontalScrollItem
                postParams={postParams}
                handleAddFlyer={handleAddFlyer}
                handleFeaturedCopyPopup={(eventId, slideImageUrl) =>
                  setFeaturedCopy({ eventId, slideImageUrl })
                }
                recommendations={recommendations}
              />
            ) : (
              <DeprecatedEventsHorizontalScrollItem
                key="scheduled-social-post-item"
                scheduledSocialPost={scheduledSocialPost}
                handleAddFlyer={handleAddFlyer}
                handleJoinCast={handleJoinCast}
                castOverride={castOverride}
              />
            ),
          ]}
        />
      )}

      {recommendationModeEnabled && (
        <div key={dateFilter} className="d-grid gap-1 mt-2">
          {scheduledSocialPost?.events?.map((event) => (
            <EventRow
              key={`recommentations-${event._id}`}
              event={event}
              defaultChecked={recommendations.includes(event._id)}
              onToggleRecommendation={handleUpdateRecommendation}
              onHideEvent={handleHideEvent}
              isScheduleDateDetailsMatch={scheduleDateDetailsLinkedEventIds.has(event._id)}
            />
          ))}
        </div>
      )}

      {currentUser?.isSuperAdmin && (
        <div className="mt-3">
          <Button variant="smooth" color="neutral" roundness="rounded" onClick={() => {}}>
            Publish Post
          </Button>
        </div>
      )}

      {!!entertainerScheduleDateDetails?.length && (
        <>
          <TitleToolbar
            text="Schedule Dates"
            size="md"
            action={
              <Button
                variant="smooth"
                color="neutral"
                roundness="rounded"
                disabled={isReprocessingScheduleDateDetails}
                onClick={() =>
                  reprocessScheduleDateDetailsAsync({
                    scheduleDateDetailsIds: entertainerScheduleDateDetails.map(
                      (date) => date.dateDetails._id,
                    ),
                  })
                }
              >
                Reprocess
              </Button>
            }
          />
          <div className="d-grid mt-3 gap-3">
            {entertainerScheduleDateDetails.map((date) => (
              <EntertainerScheduleDateDetailRow
                key={`${date.entertainer._id}-${date.date}`}
                entertainerScheduleDateDetail={date}
                onCreateEvent={setCreateEntertainerScheduleEventDateDetail}
              />
            ))}
          </div>
        </>
      )}

      <div className="mb-4" />

      {!entertainer?._id && !hideIndividualFeatureTitle && (
        <div className="text-align-center fs-6">
          <strong>⬇️&nbsp;&nbsp;Looking for an individual feature?&nbsp;&nbsp;⬇️</strong>
        </div>
      )}

      <SlideFeaturedCopyPopup
        eventId={featuredCopy.eventId}
        slideImageUrl={featuredCopy.slideImageUrl}
        onClose={() => {
          setFeaturedCopy({ eventId: '', slideImageUrl: '' });
        }}
      />

      <CreateEntertainerScheduleEventPopup
        isVisible={!!createEntertainerScheduleEventDateDetail}
        onClose={() => {
          setCreateEntertainerScheduleEventDateDetail(undefined);
        }}
        entertainerScheduleDateDetail={createEntertainerScheduleEventDateDetail}
        seriesId={scheduledSocialPost?.seriesId}
        cityId={city?._id}
        cityOptions={
          cities
            ?.filter((city) => city.eventsAreEnabled)
            .map((city) => ({ label: city.name, value: city._id })) ?? []
        }
        onCreated={refetchScheduledSocialPost}
      />
    </>
  );
};
